import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { IonicModule, IonToggle, ToggleCustomEvent } from '@ionic/angular';
import { select, Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { SettingsActions } from 'src/app/ngrx/settings/settings.actions';
import { settingsFeature } from 'src/app/ngrx/settings/settings.feature';
import { IPushChannel } from 'src/app/ngrx/settings/settings.models';

/**
 * A setup step. Only the master switch, since the individual channels belong in the
 * settings rather than in the way of someone who wants to get into the app.
 */
@Component({
  selector: 'app-wizard-push',
  standalone: true,
  imports: [IonicModule, TranslatePipe, AsyncPipe],
  template: `
    <section class="intro ion-text-center">
      <div class="icon-wrap">
        <ion-icon name="notifications-outline"></ion-icon>
        <span class="notification-dot"></span>
      </div>
      <h2>{{ 'wizard.push-notification.header' | translate }}</h2>
      <p>{{ 'wizard.push-notification.message' | translate }}</p>
    </section>

    <ion-item class="master-toggle" lines="none">
      <ion-icon slot="start" name="notifications-circle-outline"></ion-icon>
      <ion-toggle #push name="push" [checked]="push$ | async" (ionChange)="toggle($event)">
        <strong>
          {{ 'wizard.push-notification.activate' | translate }}
        </strong>
      </ion-toggle>
    </ion-item>

    <section class="channels" [class.disabled]="!push.checked">
      <div class="channels-heading">
        <h3>{{ 'settings.push.channel.header' | translate }}</h3>
        <p>{{ 'settings.push.channel.subheader' | translate }}</p>
      </div>

      <ion-list lines="none">
        <ion-item>
          <ion-icon slot="start" name="megaphone-outline"></ion-icon>
          <ion-toggle #general name="general" [disabled]="!push.checked" [checked]="(pushChannels$ | async)?.general" (ionChange)="togglePushChannel(general)">
          {{ 'settings.push.channel.general' | translate }}
          </ion-toggle>
        </ion-item>

        <ion-item>
          <ion-icon slot="start" name="barbell-outline"></ion-icon>
          <ion-toggle #sports name="sports" [disabled]="!push.checked" [checked]="(pushChannels$ | async)?.sports" (ionChange)="togglePushChannel(sports)">
            {{ 'settings.push.channel.sports' | translate }}
          </ion-toggle>
        </ion-item>

        <ion-item>
          <ion-icon slot="start" name="library-outline"></ion-icon>
          <ion-toggle #library name="library" [disabled]="!push.checked" [checked]="(pushChannels$ | async)?.library" (ionChange)="togglePushChannel(library)">
            {{ 'settings.push.channel.library' | translate }}
          </ion-toggle>
        </ion-item>

        <ion-item>
          <ion-icon slot="start" name="medkit-outline"></ion-icon>
          <ion-toggle #healthPassport name="healthPassport" [disabled]="!push.checked" [checked]="(pushChannels$ | async)?.healthPassport" (ionChange)="togglePushChannel(healthPassport)">
            {{ 'settings.push.channel.healthPassport' | translate }}
          </ion-toggle>
        </ion-item>
      </ion-list>
    </section>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: 20px;
      max-width: 520px;
      margin: 24px auto 0;
    }

    .intro {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .icon-wrap {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 104px;
      height: 104px;
      margin-bottom: 8px;
      border-radius: 32px;
      color: var(--ion-color-primary);
      background: rgba(var(--ion-color-primary-rgb), 0.12);
    }

    .icon-wrap ion-icon {
      font-size: 58px;
    }

    .notification-dot {
      position: absolute;
      top: 21px;
      right: 22px;
      width: 15px;
      height: 15px;
      border: 3px solid var(--ion-background-color, #fff);
      border-radius: 50%;
      background: var(--ion-color-danger);
    }

    h2 {
      margin: 12px 0 8px;
      font-size: 1.55rem;
      font-weight: 700;
    }

    .intro p {
      max-width: 440px;
      margin: 0;
      color: var(--ion-color-step-600, #666);
      line-height: 1.5;
    }

    .master-toggle {
      --background: rgba(var(--ion-color-primary-rgb), 0.08);
      --border-radius: 16px;
      --inner-padding-end: 16px;
      --min-height: 68px;
      --padding-start: 16px;
      overflow: hidden;
      border: 1px solid rgba(var(--ion-color-primary-rgb), 0.35);
      border-radius: 16px;
    }

    .master-toggle ion-icon {
      margin-right: 14px;
      color: var(--ion-color-primary);
      font-size: 32px;
    }

    .master-toggle ion-toggle {
      width: 100%;
    }

    .channels {
      overflow: hidden;
      border: 1px solid var(--ion-color-step-150, #ddd);
      border-radius: 16px;
      transition: opacity 160ms ease;
    }

    .channels.disabled {
      opacity: 0.55;
    }

    .channels-heading {
      padding: 16px 16px 12px;
      background: var(--ion-color-light);
    }

    .channels-heading h3 {
      margin: 0 0 4px;
      font-size: 1rem;
      font-weight: 700;
    }

    .channels-heading p {
      margin: 0;
      color: var(--ion-color-step-600, #666);
      font-size: 0.85rem;
      line-height: 1.4;
    }

    ion-list {
      padding: 0;
    }

    ion-list ion-item {
      --inner-padding-end: 16px;
      --min-height: 54px;
      --padding-start: 16px;
    }

    ion-list ion-item:not(:last-child) {
      border-bottom: 1px solid var(--ion-color-step-100, #eee);
    }

    ion-list ion-icon {
      margin-right: 14px;
      color: var(--ion-color-primary);
      font-size: 22px;
    }

    ion-list ion-toggle {
      width: 100%;
    }

    @media (max-width: 380px) {
      .icon-wrap {
        width: 88px;
        height: 88px;
      }

      .icon-wrap ion-icon {
        font-size: 50px;
      }

      .notification-dot {
        top: 17px;
        right: 17px;
      }
    }
  `
})
export class PushStepComponent {
  private readonly store = inject(Store);

  public readonly translate = inject(TranslateService);
  public readonly push$: Observable<boolean> = this.store.pipe(select(settingsFeature.selectPushEnabled));
  public readonly pushChannels$: Observable<{ [K in IPushChannel]?: boolean }> = this.store.pipe(select(settingsFeature.selectPushChannels));
  public readonly enabled = this.store.selectSignal(settingsFeature.selectPushEnabled);

  public toggle(event: ToggleCustomEvent): void {
    this.store.dispatch(SettingsActions.togglePush({ enabled: event.detail.checked }));
  }

  public togglePushChannel(event: IonToggle): void {
    const channel: IPushChannel = event.name as IPushChannel;
    this.store.dispatch(SettingsActions.togglePushChannel({ channel, enabled: event.checked }));
  }
}
